import { FC, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { CircularProgress, Typography, Box, Button, Stack, Avatar, Divider } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import { DateCalendar } from '@mui/x-date-pickers';

import './CalendlySchedulePage.scss';
import { generalSnackBarAtom, useCalendlySlots } from '../../../data-access';
import { CalendlyLifestackLogo } from '../CalendlyLifestackLogo';
import { useRecoilState } from 'recoil';

const startDate = dayjs().add(10, 'minute').toISOString(); // add 10 minutes to avoid api error because of past time
const endDate = dayjs(startDate).endOf('month').toISOString();

const getDayString = (date: string | Dayjs) => dayjs(date).format('YYYY-MM-DD');

const CalendlySchedulePage: FC = () => {
  const { calendlyIntegId } = useParams();
  const {
    calendlySlots,
    isLoading: isFetchingCalendlySlots,
    error,
  } = useCalendlySlots({
    calendlyIntegId: calendlyIntegId as string,
    startDate,
    endDate,
  });
  const [_, setGeneralSnackbar] = useRecoilState(generalSnackBarAtom);

  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());

  useEffect(() => {
    const errorMessage = (error as any)?.response?.data?.error;
    if (!errorMessage) {
      return;
    }
    setGeneralSnackbar(errorMessage);
  }, [error]);

  // Extract eventType and slots
  const eventType = calendlySlots?.eventType;
  const slots = calendlySlots?.slots || [];

  // Group slots by day
  const slotsByDay = useMemo(() => {
    const map: Record<string, typeof slots> = {};
    slots.forEach((slot) => {
      const day = getDayString(slot.start_time);
      if (!map[day]) map[day] = [];
      map[day].push(slot);
    });
    return map;
  }, [slots]);

  // Get all days with slots
  const availableDays = Object.keys(slotsByDay);

  // Time zone
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Disable days without slots
  const shouldDisableDate = (date: Dayjs) => !availableDays.includes(getDayString(date));

  // Slots for selected day
  const selectedDayStr = selectedDate ? getDayString(selectedDate) : null;
  const slotsForSelectedDay = selectedDayStr ? slotsByDay[selectedDayStr] || [] : [];

  if (isFetchingCalendlySlots) {
    return (
      <div className="calendly-schedule">
        <CircularProgress />
      </div>
    );
  }

  if (!eventType) {
    return <div className="calendly-schedule">No data found.</div>;
  }

  return (
    <div className="calendly-schedule-wrapper">
      <div className="calendly-schedule calendly-schedule--2col">
        {/* Left: Event Info Panel */}
        <div className="calendly-schedule__event-panel">
          <CalendlyLifestackLogo logoWidth="30px" />
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            {eventType.name}
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <AccessTimeIcon sx={{ fontSize: 20, color: '#7c7c7c' }} />
            <Typography variant="body2" color="textSecondary">
              {eventType.duration ? `${eventType.duration} min` : ''}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <VideocamOutlinedIcon sx={{ fontSize: 20, color: '#7c7c7c' }} />
            <Typography variant="body2" color="textSecondary">
              Web conferencing details provided upon confirmation.
            </Typography>
          </Box>
          <Box mt="auto" width="100%" display="flex" justifyContent="space-between">
            <Typography variant="caption" color="primary">
              Generated from{' '}
              <Link to="https://lifestack.ai" target="_blank">
                Lifestack
              </Link>
              , AI health calendar
            </Typography>
          </Box>
        </div>
        <Divider orientation="vertical" flexItem />
        {/* Right: Calendar & Slots Panel */}
        <div className="calendly-schedule__right-group">
          <div className="calendly-schedule__calendar-panel">
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
              Select a Date & Time
            </Typography>
            <DateCalendar
              views={['day']}
              value={selectedDate}
              onChange={setSelectedDate}
              shouldDisableDate={shouldDisableDate}
              disablePast
              className="calendly-schedule__calendar-panel__calendar"
              sx={{ bgcolor: '#fff', borderRadius: 2, boxShadow: 1, p: 1 }}
            />
            <Box mt={2}>
              <Typography variant="subtitle2" sx={{ color: '#7c7c7c' }}>
                Time zone: {timeZone}
              </Typography>
            </Box>
          </div>
          <div className="calendly-schedule__slots-panel">
            {selectedDate ? (
              <>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  {selectedDate.format('dddd, MMM D')}
                </Typography>
                <Stack spacing={2}>
                  {slotsForSelectedDay.length ? (
                    slotsForSelectedDay.map((slot, idx) => (
                      <Button
                        key={idx}
                        variant="outlined"
                        className="calendly-schedule__slots-panel__btn"
                        onClick={() => {
                          window.open(slot.scheduling_url, '_blank');
                        }}
                      >
                        {dayjs(slot.start_time).format('HH:mm')}
                      </Button>
                    ))
                  ) : (
                    <Typography>No available slots for this day.</Typography>
                  )}
                </Stack>
              </>
            ) : (
              <Typography>Select a date to see available time slots.</Typography>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendlySchedulePage;
